import React, { type PropsWithChildren, useEffect, useState } from "react";
import { api, IAuthSite, IContext, IPreferences, User } from "../logic/api";
import { options } from "./options"
import { extractObjectFromCookie, retrieveObject, retrieveValue, storeObject, storeValue } from "./storage";

import { useHistory } from "react-router-dom";
import { SnackAlert } from "../components/notifications/snack/SnackAlert";
import ErrorDialog from "../components/notifications/errorDialog/ErrorDialog";
import FadeIn from "../components/animations/FadeIn";
import TwoFactor from "../components/twoFactor/twoFactor";
import { CircularProgress } from "@mui/material";
import usePaths from "./paths";
import { useLDClient } from "@launchdarkly/react-sdk";
import { getLDContext } from "../services/launch-darkly";
import { useTranslation } from "react-i18next";
import { SessionTimeoutProvider } from "../components/SessionTimeoutProvider";
import { SESSION_LAST_ACTIVITY_KEY, SESSION_LOGOUT_SIGNAL_KEY } from "@/utility/sessionStorageKeys";

export interface IOptions {
    environment: string;
    launchDarklyKey: string;
    zendeskKey: string;
    apiUrl: string;
}
export interface IUser {
    firstName: string;
    lastName: string;
    database: string;
    identity: string;
    twoFactorEnabled: boolean;
    clientCode: string;
}
export interface ISession {
    site: IAuthSite | null;
    credential: IUser | null;
    context: IContext;
}
export interface ISessionState {
    current: ISession;
    options: IOptions;
    login: (token: string, usr: IUser) => void,
    logout: () => void,
    setPreferences: (prefs: IPreferences) => void,
    getContextWithToken: (token: string) => IContext,
    setTwoFactorEnabled: (enabled: boolean) => void,
    setUser: (user: User) => void
    switchSite: (homeLocationCode: string) => void;
}

const defaultSessionState: ISessionState = {
    current: {
        context: {} as IContext,
        site: null,
        credential: null,
    },
    options,
    login: () => console.log("no provider for session"),
    logout: () => console.log("no provider for session"),
    setPreferences: () => { console.log("no provider for session"); },
    getContextWithToken: () => { console.log("no provider for session"); return {} as IContext; },
    setTwoFactorEnabled: () => { console.log("no provider for session"); },
    setUser: () => { console.log("no provider for session"); },
    switchSite: () => { console.log("no provider for session"); },
};

export const SessionContext = React.createContext<ISessionState>(defaultSessionState)

export const useSession = () => {
    const context = React.useContext(SessionContext);
    if (!context) {
        throw new Error('useSession must be used within the Session provider');
    }
    return context;
};


export const Session = (props: PropsWithChildren) => {
    const paths = usePaths();
    const ldClient = useLDClient();
    const selectSiteRequest = api.hooks.auth.select();
    const { t } = useTranslation();

    const url = defaultSessionState.options.apiUrl;

    const getContextWithToken = (token: string) => {
        return {
            url: url,
            headers: {
                authorization: `Bearer ${token}`
            }
        }
    };

    const [token, setToken] = useState<string>(retrieveValue("userToken", ""));
    const [credential, setCredential] = useState<IUser | null>(retrieveObject("userCredential", null));
    const [site, setSite] = useState<IAuthSite>({} as IAuthSite);
    const [authValidated, setAuthValidated] = useState<boolean | null>(credential == null || token.length == 0 ? false : null);
    const [homeLocationCode, setHomeLocationCode] = useState<string | undefined>(retrieveValue("homeLocationCode", undefined));


    const history = useHistory();

    useEffect(() => {
        const authResult = extractObjectFromCookie("authResult", null);

        if (!authResult || !authResult.token || !authResult.firstName) {
            return;
        }

        setToken(authResult.token);
        setCredential({ ...credential as IUser, firstName: authResult.firstName });
    }, []);

    useEffect(() => {
        let cancelled = false;

        if (!token) {
            return;
        }

        if (!credential || !credential.identity) {
            return;
        }

        selectSiteRequest.send(getContextWithToken(token), { identity: credential.identity, homeLocationCode: homeLocationCode }, (result) => {
            if (cancelled) {
                return;
            }

            if (!result) {
                setAuthValidated(false);
                setCredential(null);
                setToken("");
                return;
            }

            setToken(result.token);
            setSite(result.site);
            setAuthValidated(true);

            storeValue("userToken", result.token);
            storeObject("userCredential", credential);

            if (credential.identity == "admin") {
                if (window.location.pathname != paths.admin) {
                    history.push(paths.admin);
                }
            } else {
                const authPaths = ["/", paths.tenants];
                if (authPaths.includes(window.location.pathname)) {
                    history.push(`${credential.clientCode}${paths.home}`.toLowerCase());
                }
            }

            // Explicitly use LD Client as can't use `useLDClient` in useEffect
            const ldContext = getLDContext(result.site.user, result.site);
            ldClient?.identify(ldContext);
        });

        return () => {
            cancelled = true;
        }
    }, [credential, homeLocationCode]);


    if (authValidated == null) {
        return <div style={{ padding: 10 }}><CircularProgress size={14}></CircularProgress> Loading..</div>
    }

    const authenticatedSite = authValidated == true ? site : null;

    return <SessionContext.Provider value={
        {
            current: {
                context: getContextWithToken(token),
                credential: credential,
                site: authValidated == true ? authenticatedSite : null,
            },
            options: defaultSessionState.options,
            setPreferences: (prefs: IPreferences) => {
                const newSiteState = { ...site, preferences: prefs };
                setSite(newSiteState);
            },
            login: (token: string, usr: IUser) => {
                setToken(token);
                setCredential(usr);
                setAuthValidated(false);
                setHomeLocationCode("");
                storeValue("homeLocationCode", "");
                storeValue("userToken", token);
                storeObject("userCredential", credential);
            },
            logout: () => {
                setToken("");
                setCredential(null);
                setAuthValidated(false);
                storeValue("userToken", "");
                storeObject("userCredential", null);
                storeObject("site", null);
                localStorage.removeItem(SESSION_LAST_ACTIVITY_KEY);
                localStorage.removeItem(SESSION_LOGOUT_SIGNAL_KEY);
                history.push("/");
            },
            getContextWithToken: (token: string) => {
                return getContextWithToken(token);
            },
            setTwoFactorEnabled: (enabled: boolean) => {
                setCredential({ ...credential as IUser, twoFactorEnabled: enabled });
            },
            setUser: (user: User) => {
                if (site) {
                    setSite({ ...site, user: user });
                }
            },
            switchSite: (homeLocationCode: string) => {
                setHomeLocationCode(homeLocationCode);
                storeValue("homeLocationCode", homeLocationCode);
            }
        }
    }>
        <TwoFactor>
            <FadeIn>
                <SessionTimeoutProvider>
                    <div>{props.children}</div>
                </SessionTimeoutProvider>
            </FadeIn>
            {credential != null && (<SnackAlert id="welcomeBack" message={t('welcome.user', { firstName: credential.firstName })}></SnackAlert>)}
            <ErrorDialog err={selectSiteRequest.err}></ErrorDialog>
        </TwoFactor>
    </SessionContext.Provider>
}
